import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable"
import { useDefaultLayout } from "react-resizable-panels"
import { useSyncedHeights } from "@/hooks/use-synced-heights"
import { AgentForm } from "./Form"
import { EditSidebar } from "./EditSidebar"

// Height-synced element pairs: [form-side, sidebar-side]
const SIZE_PAIRS = [
  ["trigger", "test-trigger"],
  ["data", "test-data"],
  ["request", "test-request"],
]

export function EditView({ agent, workspace_slug, onSubmit, scrollContainerRef, runTestCount }) {
  const sentinelRef = useRef(null)
  const [panelAtHeader, setPanelAtHeader] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = scrollContainerRef?.current
    if (!sentinel || !root) return

    const observer = new IntersectionObserver(
      ([entry]) => setPanelAtHeader(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [scrollContainerRef])

  const { defaultLayout, onLayoutChange } = useDefaultLayout({
    id: "agent-edit-panels",
    panelIds: ["agent-form", "agent-sidebar"]
  })

  useSyncedHeights(SIZE_PAIRS, true)

  // Poll for analysis, extraction, or trigger match completion
  const analysisInProgress = agent.analysis_status === "pending" || agent.analysis_status === "analyzing"
  const extractionInProgress = agent.preview_extraction_status === "pending" || agent.preview_extraction_status === "extracting"
  const triggerMatchInProgress = agent.active_trigger_match_status === "pending" || agent.active_trigger_match_status === "evaluating"

  useEffect(() => {
    if (!analysisInProgress && !extractionInProgress && !triggerMatchInProgress) return

    const interval = setInterval(() => {
      router.reload({ only: ["agent"] })
    }, 3000)

    return () => clearInterval(interval)
  }, [analysisInProgress, extractionInProgress, triggerMatchInProgress])

  return (
    <>
    <div ref={sentinelRef} className="h-0" />
    <ResizablePanelGroup
      orientation="horizontal"
      className="!h-auto"
      defaultLayout={defaultLayout ?? { "agent-form": 65, "agent-sidebar": 35 }}
      onLayoutChange={onLayoutChange}
      disableCursor
    >
      <ResizablePanel id="agent-form" minSize="300px" className="flex flex-col">
        <AgentForm
          key={agent.updated_at}
          agent={agent}
          onSubmit={onSubmit}
        />
      </ResizablePanel>
      <ResizableHandle className="-mx-1.5 relative z-10 cursor-col-resize" data-scrolled={panelAtHeader ? "" : undefined} />
      <ResizablePanel id="agent-sidebar" minSize="300px">
        <EditSidebar
          agent={agent}
          workspace_slug={workspace_slug}
          analysisInProgress={analysisInProgress}
          extractionInProgress={extractionInProgress}
          triggerMatchInProgress={triggerMatchInProgress}
          runTestCount={runTestCount}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
    </>
  )
}
